import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, ElementRef, HostListener, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { A11yModule } from '@angular/cdk/a11y';
import { filter } from 'rxjs';
import { AppAuthService } from './common/auth';
import {
  HelpChatService,
  type ProposedAction,
  type ChatSession,
  type ChatMessageRecord,
} from './common/service/help-chat.service';
import { ChatRequestError, chatErrorFromSseEvent, chatErrorKey } from './common/service/help-chat-errors';
import { ActionRegistryService } from './common/service/action-registry.service';
import { TranslationService } from './common/service/translation-service.service';
import { parseHelpChatMarkdown, type HelpChatMarkdownPart } from './help-chat-markdown.util';
import { HelpChatResetDialogComponent } from './help-chat-reset-dialog.component';
import { InfoTextBoxComponent } from './common/info-text-box.component';

/** Set on <body> while the widget exists; scopes the launcher-clearance layout rules. */
export const HELP_CHAT_BODY_CLASS = 'help-chat-active';

interface UiChatMessage {
  role: 'user' | 'ai';
  text: string;
  createdAt: string;
  actions?: Array<ProposedAction & { used?: boolean }>;
  streaming?: boolean;
  sources?: Array<{ path: string; url: string }>;
  failed?: boolean;
  idempotencyKey?: string;
}

interface UiChatPart {
  text?: string;
  action?: ProposedAction & { used?: boolean };
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  linkUrl?: string;
  headingLevel?: 2 | 3 | 4;
  horizontalRule?: boolean;
  listItem?: boolean;
}

@Component({
  selector: 'app-help-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, A11yModule],
  templateUrl: './help-chat-widget.component.html',
  styleUrls: ['./help-chat-widget.component.scss'],
})
export class HelpChatWidgetComponent implements OnDestroy {
  protected readonly auth = inject(AppAuthService);
  private readonly chat = inject(HelpChatService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly translation = inject(TranslationService);
  private readonly actionRegistry = inject(ActionRegistryService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly document = inject(DOCUMENT);

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLElement>;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;
  protected readonly open = signal(false);
  protected readonly pageType = signal('unknown');
  protected readonly loading = signal(false);
  protected readonly sending = signal(false);
  protected readonly messages = signal<UiChatMessage[]>([]);
  protected readonly hasOlderMessages = signal(false);
  protected readonly loadingOlder = signal(false);
  protected readonly newMessageCount = signal(0);
  protected draft = '';

  /** Non-fatal problem shown above the chat body (history unavailable, stream
   *  gave up reconnecting, …). The chat itself stays usable. */
  protected readonly notice = signal<string | null>(null);
  protected readonly connectionState = signal<'connected' | 'connecting' | 'disconnected'>('connecting');

  private closeStream?: () => void;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private reconnectAttempts = 0;
  private static readonly maxReconnectAttempts = 6;
  private streamStarted = false;
  private streamConnecting = false;
  private initializing = false;
  private streamTextBuffer = '';
  private streamSources: Array<{ path: string; url: string }> = [];
  private streamActions: ProposedAction[] = [];
  private localHandlerInflight = false;
  private abortController?: AbortController;
  private historyLoaded = false;
  private oldestMessageId?: string;
  private historyMessages: UiChatMessage[] = [];
  private turnOwnedByLocal = false;
  private readonly minLoadingMs = 500;
  // Keys: `${messageId}|${actionId}|${canonJson(params)}` — chips for
  // non-idempotent actions that have already fired a successful click.
  private readonly usedChipKeys = new Set<string>();
  private readonly seenStreamMessageIds = new Set<string>();
  // Previous-session banner: server reports the most recently auto-archived
  // session via GET /api/chat/session.previousSession; signal drives the
  // template's @if block.
  protected readonly previousSession = signal<NonNullable<ChatSession['previousSession']> | null>(null);
  private pingTimer?: ReturnType<typeof setInterval>;

  constructor() {
    this.document.body.classList.add(HELP_CHAT_BODY_CLASS);
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const path = e.urlAfterRedirects.split('?')[0];
      this.pageType.set(path.split('/').filter(Boolean)[0] || 'dashboard');
    });
  }

  @HostListener('document:keydown.escape')
  protected closeWithEscape(): void {
    if (this.open()) this.toggleOpen();
  }

  ngOnDestroy(): void {
    this.closeStream?.();
    this.stopPingTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.document.body.classList.remove(HELP_CHAT_BODY_CLASS);
  }

  private startPingTimer(): void {
    if (this.pingTimer) return;
    void this.chat.ping();
    this.pingTimer = setInterval(() => void this.chat.ping(), 5 * 60_000);
  }

  private stopPingTimer(): void {
    if (!this.pingTimer) return;
    clearInterval(this.pingTimer);
    this.pingTimer = undefined;
  }

  // Bound to a (click) — Angular does not await the returned promise, so an
  // uncaught rejection here would escape to the global ErrorHandler and take
  // the whole page down. Everything inside is handled.
  protected async switchToPreviousSession(): Promise<void> {
    const prev = this.previousSession();
    if (!prev) return;
    try {
      await this.chat.activateSession(prev.id);
    } catch (err) {
      this.showNotice(err, '[help-chat] failed to switch session');
      return;
    }
    this.previousSession.set(null);
    this.notice.set(null);
    // Reset per-session widget state so history reloads from the new active session.
    this.historyLoaded = false;
    this.historyMessages = [];
    this.oldestMessageId = undefined;
    this.hasOlderMessages.set(false);
    this.streamActions = [];
    this.streamTextBuffer = '';
    this.streamSources = [];
    this.usedChipKeys.clear();
    try {
      await this.loadRecentMessages();
    } catch (err) {
      this.showNotice(err, '[help-chat] failed to load messages after switch');
    }
  }

  /** Translate a thrown chat error for display, logging the internal detail. */
  private errorText(err: unknown): string {
    if (err instanceof ChatRequestError) {
      console.warn('[help-chat] status=%s code=%s reason=%s', err.status, err.code, err.reason);
    }
    return this.translate.instant(chatErrorKey(err));
  }

  /** Failure text for a streaming turn, separated from a half-finished sentence. */
  private failureText(err: unknown, streamedSoFar: string): string {
    const text = this.errorText(err);
    return streamedSoFar.trim() ? `\n\n${text}` : text;
  }

  private showNotice(err: unknown, logLabel: string): void {
    console.warn(logLabel, err);
    this.notice.set(this.errorText(err));
  }

  protected retryInitialize(): void {
    this.notice.set(null);
    this.historyLoaded = false;
    this.reconnectAttempts = 0;
    void this.initialize();
  }

  protected dismissPreviousBanner(): void {
    this.previousSession.set(null);
  }

  protected async resetSession(): Promise<void> {
    const confirmed = await new Promise<boolean>((resolve) => {
      const ref = this.dialog.open(HelpChatResetDialogComponent, {
        width: 'min(420px, calc(100vw - 32px))',
        panelClass: 'help-chat-reset-dialog-panel',
        autoFocus: 'dialog',
        restoreFocus: true,
      });
      ref.afterClosed().subscribe((result) => resolve(result === true));
    });
    if (!confirmed) return;
    try {
      this.closeStream?.();
      this.closeStream = undefined;
      this.abortController?.abort();
      this.abortController = undefined;
      await this.chat.resetSession();
      this.messages.set([]);
      this.historyMessages = [];
      this.historyLoaded = false;
      this.oldestMessageId = undefined;
      this.hasOlderMessages.set(false);
      this.previousSession.set(null);
      this.notice.set(null);
      this.streamStarted = false;
      this.reconnectAttempts = 0;
      await this.loadRecentMessages();
      void this.ensureStream();
      this.snackBar.open(
        this.translate.instant('Help_chat_reset_success_tr'),
        this.translate.instant('Help_chat_dismiss_tr'),
        { duration: 3500 }
      );
    } catch (err) {
      this.showNotice(err, '[help-chat] failed to reset session');
      this.snackBar.open(
        this.translate.instant('Help_chat_reset_failure_tr'),
        this.translate.instant('Help_chat_dismiss_tr'),
        { duration: 5000 }
      );
    }
  }

  protected toggleOpen() {
    this.open.update((v) => !v);
    if (!this.open()) {
      this.loading.set(false);
      this.notice.set(null);
      this.reconnectAttempts = 0;
      this.closeStream?.();
      this.closeStream = undefined;
      this.abortController?.abort();
      this.abortController = undefined;
      this.streamStarted = false;
      this.historyLoaded = false;
      this.historyMessages = [];
      this.oldestMessageId = undefined;
      this.hasOlderMessages.set(false);
      this.messages.set([]);
      this.stopPingTimer();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = undefined;
      }
      return;
    }
    this.startPingTimer();
    this.connectionState.set('connecting');

    setTimeout(() => this.messageInput?.nativeElement.focus(), 0);
    void this.initialize();
  }

  // Called from a (click) via `void` — nothing awaits it, so every step is
  // individually contained. A failing sub-step degrades the widget instead of
  // rejecting into Angular's global ErrorHandler ("Internal application error").
  private async initialize() {
    if (this.initializing) return;
    this.initializing = true;
    this.loading.set(true);
    const startedAt = Date.now();
    try {
      // Bind first: even with no session and no history, the input must work.
      await Promise.resolve();

      try {
        const sess = await this.chat.getSession();
        this.previousSession.set(sess?.previousSession ?? null);
        // The sweeper can archive the active session while the widget is
        // closed. In that case GET /session returns 404 and cannot include the
        // archived preview, so restore the most recent inactivity archive on
        // reopen before loading history.
        if (!sess) {
          const archived = (await this.chat.listSessions())
            .filter((entry) => entry.status === 'archived' && entry.archiveReason === 'inactivity')
            .sort(
              (a, b) =>
                new Date(b.archivedAt ?? b.createdAt).getTime() - new Date(a.archivedAt ?? a.createdAt).getTime()
            )[0];
          if (archived) await this.chat.activateSession(archived.id);
        }
      } catch (err) {
        // Only costs the "switch back" banner — not worth blocking the chat.
        console.warn('[help-chat] failed to load session', err);
      }

      try {
        await this.loadRecentMessages();
      } catch (err) {
        this.showNotice(err, '[help-chat] failed to load history');
      }

      void this.ensureStream();
    } catch (err) {
      this.showNotice(err, '[help-chat] initialization failed');
    } finally {
      const elapsed = Date.now() - startedAt;
      const extraDelay = this.minLoadingMs - elapsed;
      if (extraDelay > 0) await new Promise((resolve) => setTimeout(resolve, extraDelay));
      this.loading.set(false);
      this.initializing = false;
    }
  }

  protected handleComposerKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      void this.sendMessage(keyboardEvent);
    }
  }

  protected resizeComposer(): void {
    const textarea = this.messageInput?.nativeElement;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = Math.min(280, Math.floor(window.innerHeight * 0.5));
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  protected retryMessage(index: number): void {
    const items = this.messages();
    const failed = items[index];
    const previous = items[index - 1];
    if (!failed?.failed || previous?.role !== 'user' || this.sending()) return;
    this.messages.set(items.slice(0, index - 1));
    void this.sendMessage(undefined, previous.text, previous.idempotencyKey);
  }

  protected async sendMessage(event?: Event, retryText?: string, retryKey?: string): Promise<void> {
    event?.preventDefault();
    const text = (retryText ?? this.draft).trim();
    if (!text || this.sending()) return;
    this.draft = '';
    setTimeout(() => this.resizeComposer(), 0);
    const idempotencyKey = retryKey ?? crypto.randomUUID();
    const createdAt = new Date().toISOString();
    this.messages.update((items) => [
      ...items,
      { role: 'user', text, createdAt, idempotencyKey },
      { role: 'ai', text: '', createdAt, streaming: true },
    ]);
    // Give the user a clear transition to the new turn. Subsequent stream
    // deltas use an instant scroll so the browser does not restart an
    // animation for every token.
    this.scrollMessages('smooth');
    this.sending.set(true);
    // The shared SSE stream mirrors this turn to other tabs. Mark this turn
    // as local so the same response is not appended a second time here.
    this.localHandlerInflight = true;
    this.abortController = new AbortController();
    const answerIndex = this.messages().length - 1;
    let answer = '';
    const cites: Array<{ path: string; url: string }> = [];
    const actions: ProposedAction[] = [];
    try {
      await this.chat.streamMessage(
        text,
        {
          pageUrl: window.location.href,
          pageType: this.pageType(),
          customerId: this.auth.user?.email ?? 'unknown',
          // Profile language (User.language), synced into the UI by UserLanguageSyncService (app#318).
          locale: this.translation.language,
          availableActions: this.availableActionsForCurrentPage(),
        },
        idempotencyKey,
        {
          onMeta: (meta) => {
            this.messages.update((items) =>
              items.map((m, i) => {
                if (i === answerIndex && meta?.createdAt) return { ...m, createdAt: meta.createdAt };
                if (i === answerIndex - 1 && meta?.userCreatedAt) return { ...m, createdAt: meta.userCreatedAt };
                return m;
              })
            );
          },
          onDelta: (chunk) => {
            answer += chunk;
            this.updateAnswer(answerIndex, answer);
          },
          onCitation: (c) => {
            if (c.sourcePath && !cites.some((cite) => cite.path === c.sourcePath)) {
              cites.push({ path: c.sourcePath, url: c.url || this.sourceUrl(c.sourcePath) });
              this.messages.update((items) =>
                items.map((m, i) => (i === answerIndex ? { ...m, sources: this.uniqueSources(cites) } : m))
              );
            }
          },
          onAction: (a) => actions.push(a),
          onDone: () => {
            let finalText = stripInlineActionTags(answer.trim()) || this.translate.instant('Help_chat_empty_answer_tr');
            this.updateAnswer(answerIndex, finalText, actions, cites);
          },
          onError: (err) => this.updateAnswer(answerIndex, this.failureText(err, answer), [], undefined, true),
        }
      );
    } catch (err) {
      this.updateAnswer(answerIndex, this.failureText(err, answer), [], undefined, true);
    } finally {
      this.messages.update((items) => items.map((m, i) => (i === answerIndex ? { ...m, streaming: false } : m)));
      this.sending.set(false);
      this.localHandlerInflight = false;
      this.abortController = undefined;
      this.scrollMessages();
    }
  }

  protected stopGenerating(): void {
    this.abortController?.abort();
    this.sending.set(false);
  }

  private updateAnswer(
    index: number,
    text: string,
    actions: ProposedAction[] = [],
    sources?: Array<{ path: string; url: string }>,
    failed = false
  ): void {
    const wasNearBottom = this.isNearBottom();
    const messageActions = actions.map((a) => ({ ...a, used: this.isChipUsed(a) }));
    this.messages.update((items) =>
      items.map((m, i) =>
        i === index
          ? {
              ...m,
              text,
              actions: messageActions,
              failed,
              ...(sources ? { sources: this.uniqueSources(sources) } : {}),
            }
          : m
      )
    );
    if (wasNearBottom) this.scrollMessages();
    else this.newMessageCount.update((count) => count + 1);
  }

  private isNearBottom(): boolean {
    const el = this.messagesContainer?.nativeElement;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }

  /** Keeps action buttons at the offsets supplied by chat-service while
   * rendering them as real Angular controls rather than injected HTML. */
  protected messageParts(message: UiChatMessage): UiChatPart[] {
    if (!message.actions?.length) return [{ text: message.text }];
    const parts: UiChatPart[] = [];
    let cursor = 0;
    for (const action of [...message.actions].sort((a, b) => a.offset - b.offset)) {
      const offset = Math.max(cursor, Math.min(action.offset, message.text.length));
      if (offset > cursor) parts.push({ text: message.text.slice(cursor, offset) });
      parts.push({ action });
      cursor = offset;
    }
    if (cursor < message.text.length) parts.push({ text: message.text.slice(cursor) });
    return parts;
  }

  protected messageTime(message: UiChatMessage): string {
    const date = new Date(message.createdAt);
    return Number.isNaN(date.getTime())
      ? ''
      : new Intl.DateTimeFormat(this.translate.currentLang || navigator.language || 'en', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: false,
        }).format(date);
  }

  protected shouldShowDay(message: UiChatMessage, index: number): boolean {
    if (index === 0) return true;
    const previous = this.messages()[index - 1];
    return this.dayKey(previous.createdAt) !== this.dayKey(message.createdAt);
  }

  protected messageDayLabel(message: UiChatMessage): string {
    const date = new Date(message.createdAt);
    if (Number.isNaN(date.getTime())) return '';
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (this.dayKey(date.toISOString()) === this.dayKey(today.toISOString())) return 'Today';
    if (this.dayKey(date.toISOString()) === this.dayKey(yesterday.toISOString())) return 'Yesterday';
    return new Intl.DateTimeFormat(this.translate.currentLang || navigator.language || 'en', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private dayKey(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  protected formattedTextParts(text: string): UiChatPart[] {
    return parseHelpChatMarkdown(text) as Array<UiChatPart & HelpChatMarkdownPart>;
  }

  protected scrollMessages(behavior: ScrollBehavior = 'auto'): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior });
    }, 0);
  }

  protected markMessagesRead(): void {
    if (this.isNearBottom()) this.newMessageCount.set(0);
  }

  // Stub: pages can override this in the future to declare context-specific
  // actions (e.g. `[{id: 'navigate-suppliers-acme', basedOn: 'navigate',
  // params: {path: '/suppliers/acme-001'}, label: 'View ACME'}]`). For now,
  // every page opts into the safe-by-default global actions.
  private availableActionsForCurrentPage(): Array<{ id: string }> {
    return [
      { id: 'contact-support' },
      { id: 'change-password' },
      { id: 'change-language' },
      { id: 'manage-notifications' },
    ];
  }

  private isChipUsed(a: ProposedAction): boolean {
    const key = `${a.messageId}|${a.id}|${canonJson(a.params ?? {})}`;
    return this.usedChipKeys.has(key);
  }

  private async loadRecentMessages() {
    if (this.historyLoaded) return;
    const history = await this.chat.getMessages(6);
    const records = this.sortMessageRecords(history.messages || []);
    this.historyMessages = this.toUiMessages(records);
    this.oldestMessageId = records[0]?.id;
    this.hasOlderMessages.set(records.length === 6);
    this.historyLoaded = true;
    this.messages.set(this.historyMessages.map((m) => ({ ...m })));
    this.scrollMessages();
  }

  protected async loadOlderMessages(): Promise<void> {
    if (this.loadingOlder() || !this.oldestMessageId) return;
    const container = this.messagesContainer?.nativeElement;
    const previousHeight = container?.scrollHeight ?? 0;
    this.loadingOlder.set(true);
    try {
      const history = await this.chat.getMessages(6, this.oldestMessageId);
      const records = this.sortMessageRecords(history.messages || []);
      if (records.length > 0) {
        this.oldestMessageId = records[0].id;
        this.messages.update((current) => [...this.toUiMessages(records), ...current]);
      }
      this.hasOlderMessages.set(records.length === 6);
      setTimeout(() => {
        if (container) container.scrollTop += container.scrollHeight - previousHeight;
      }, 0);
    } catch (err) {
      this.showNotice(err, '[help-chat] failed to load earlier messages');
    } finally {
      this.loadingOlder.set(false);
    }
  }

  private sortMessageRecords(records: ChatMessageRecord[]): ChatMessageRecord[] {
    return records
      .filter((m) => !(m.role === 'assistant' && m.streamingStatus === 'streaming'))
      .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
  }

  private toUiMessages(records: ChatMessageRecord[]): UiChatMessage[] {
    return records.map((m) => {
      if (m.role !== 'assistant')
        return {
          role: 'user' as const,
          text: m.content || '',
          createdAt: m.createdAt,
          idempotencyKey: m.idempotencyKey ?? undefined,
        };
      // Assistant rows may carry `actionsProposed` with a per-action
      // `latestOutcome` from the server-side join (Task 9). If actions are
      // present, render the bubble as HTML with chips; non-idempotent
      // chips that already fired a successful click start in the used state.
      const proposed = m.actionsProposed ?? [];
      const sanitizedText =
        stripInlineActionTags(m.content || '') ||
        (m.streamingStatus === 'error'
          ? this.translate.instant('Help_chat_error_provider_tr')
          : this.translate.instant('Help_chat_empty_answer_tr'));
      const sources = this.uniqueSources(
        m.citations?.map((citation) => ({
          path: citation.sourcePath,
          url: citation.url || this.sourceUrl(citation.sourcePath),
        })) ?? this.sourcesFromText(m.content).map((path) => ({ path, url: this.sourceUrl(path) }))
      );
      if (proposed.length === 0) return { role: 'ai' as const, text: sanitizedText, createdAt: m.createdAt, sources };
      const chips = proposed.map((a) => {
        // Opening support is a safe repeatable action (it only opens the
        // user's mail client), so do not permanently disable it after a
        // previous session's click. Other non-idempotent actions stay guarded.
        // Chips are repeatable controls. A successful click must not make a
        // historical answer permanently unusable after reopening the chat.
        return { ...a, messageId: m.id, used: false };
      });
      return { role: 'ai' as const, text: sanitizedText, actions: chips, createdAt: m.createdAt, sources };
    });
  }

  private uniqueSources(sources: Array<{ path: string; url: string }>): Array<{ path: string; url: string }> {
    return [...new Map(sources.filter((source) => source.path.trim()).map((source) => [source.path, source])).values()];
  }

  private sourcesFromText(text: string): string[] {
    const match = text.match(/(?:^|\n)Sources:\s*\n((?:\s*[•*-].*(?:\n|$))+)/i);
    return match
      ? match[1]
          .split('\n')
          .map((line) => line.replace(/^\s*[•*-]\s*/, ''))
          .filter(Boolean)
      : [];
  }

  protected sourceUrl(sourcePath: string): string {
    const source = sourcePath.replace(/^\/+/, '');
    if (/^https?:\/\//i.test(source)) return source;
    return source;
  }

  protected openCitation(url: string, event: Event): void {
    event.preventDefault();
    InfoTextBoxComponent.openHelp(url);
  }

  private async ensureStream() {
    if (this.streamStarted || this.streamConnecting || !this.open()) return;
    this.streamConnecting = true;
    this.streamStarted = true;
    try {
      this.closeStream = await this.chat.openStream({
        // Contained: a throw in here would break the reader loop and be
        // reported as a dropped connection, hiding a rendering bug behind an
        // endless reconnect.
        onEvent: (event, payload) => {
          try {
            this.handleStreamEvent(event, payload);
          } catch (err) {
            console.warn('[help-chat] could not handle stream event', event, err);
          }
        },
        onError: () => this.scheduleReconnect(),
      });
      this.connectionState.set('connected');
      // Connected — a later drop starts its backoff from zero again.
      this.reconnectAttempts = 0;
      this.notice.set(null);
    } catch (err) {
      console.warn('[help-chat] stream connect failed', err);
      this.scheduleReconnect();
    } finally {
      this.streamConnecting = false;
    }
  }

  private scheduleReconnect() {
    this.connectionState.set('disconnected');
    this.closeStream?.();
    this.closeStream = undefined;
    this.streamStarted = false;
    this.streamConnecting = false;
    this.historyLoaded = false;
    if (!this.open()) return;
    if (this.reconnectTimer) return;
    // Exponential backoff, then stop and say so. Retrying every 2.5 s forever
    // hammers the service through an outage and leaves the user staring at a
    // chat that silently never reconnects.
    if (this.reconnectAttempts >= HelpChatWidgetComponent.maxReconnectAttempts) {
      this.notice.set(this.translate.instant('Help_chat_error_disconnected_tr'));
      return;
    }
    const delay = Math.min(30_000, 2500 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    this.connectionState.set('connecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.ensureStream();
    }, delay);
  }

  private handleStreamEvent(event: string, payload: any) {
    if (event === 'origin-user' && payload?.messageId) {
      if (this.seenStreamMessageIds.has(payload.messageId)) return;
      this.seenStreamMessageIds.add(payload.messageId);
    }
    if (event === 'origin-user') {
      this.turnOwnedByLocal = this.localHandlerInflight;
      this.streamTextBuffer = '';
      this.streamSources = [];
      this.streamActions = [];
      if (this.turnOwnedByLocal) return;
      this.messages.update((items) => [
        ...items,
        { role: 'user', text: String(payload?.text || ''), createdAt: payload?.createdAt || new Date().toISOString() },
        { role: 'ai', text: '', createdAt: new Date().toISOString(), streaming: true },
      ]);
      return;
    }
    if (event === 'meta' && payload?.createdAt) {
      this.messages.update((items) =>
        items.map((m, i) => {
          if (i === items.length - 1) return { ...m, createdAt: payload.createdAt };
          if (i === items.length - 2 && payload.userCreatedAt) return { ...m, createdAt: payload.userCreatedAt };
          return m;
        })
      );
      return;
    }
    if (this.turnOwnedByLocal) {
      if (event === 'done' || event === 'error') this.turnOwnedByLocal = false;
      return;
    }
    if (event === 'delta') {
      this.streamTextBuffer += payload?.text || '';
      const idx = this.messages().length - 1;
      this.updateAnswer(idx, this.streamTextBuffer);
      return;
    }
    if (event === 'citation') {
      const source = payload?.sourcePath;
      if (source && !this.streamSources.includes(source)) {
        this.streamSources.push({ path: source, url: payload?.url || this.sourceUrl(source) });
        this.messages.update((items) =>
          items.map((m, i) => (i === items.length - 1 ? { ...m, sources: this.uniqueSources(this.streamSources) } : m))
        );
      }
      return;
    }
    if (event === 'action') {
      this.streamActions.push(payload as ProposedAction);
      return;
    }
    if (event === 'done') {
      let finalText = this.streamTextBuffer.trim();
      if (!finalText) finalText = this.translate.instant('Help_chat_empty_answer_tr');
      finalText = stripInlineActionTags(finalText);
      if (this.streamActions.length > 0) {
        this.updateAnswer(this.messages().length - 1, finalText, this.streamActions);
      } else {
        this.updateAnswer(this.messages().length - 1, finalText);
      }
      this.messages.update((items) => items.map((m, i) => (i === items.length - 1 ? { ...m, streaming: false } : m)));
      this.streamTextBuffer = '';
      this.streamSources = [];
      this.streamActions = [];
      if (payload?.messageId) this.seenStreamMessageIds.delete(payload.messageId);
      return;
    }
    if (event === 'error') {
      // Passive path (another tab drives the turn): the placeholder bubble from
      // `origin-user` is already there, so overwrite it with the mapped message.
      const failed = chatErrorFromSseEvent(payload);
      const text = this.failureText(failed, this.streamTextBuffer);
      this.updateAnswer(this.messages().length - 1, this.streamTextBuffer + text);
      this.messages.update((items) => items.map((m, i) => (i === items.length - 1 ? { ...m, streaming: false } : m)));
      this.streamTextBuffer = '';
      this.streamSources = [];
      this.streamActions = [];
    }
  }

  protected actionButtonClick(action: ProposedAction & { used?: boolean }, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const chip = event.currentTarget as HTMLButtonElement | null;
    if (!chip || chip.disabled) return;
    chip.dataset['messageId'] = action.messageId;
    chip.dataset['actionId'] = action.id;
    chip.dataset['actionParams'] = JSON.stringify(action.params ?? {});
    void this.runChipClick(chip).catch((err) => console.warn('[help-chat] action click failed', err));
  }

  private async runChipClick(chip: HTMLButtonElement): Promise<void> {
    const messageId = chip.dataset['messageId'] ?? '';
    const actionId = chip.dataset['actionId'] ?? '';
    let params: Record<string, unknown>;
    try {
      params = JSON.parse(chip.dataset['actionParams'] ?? '{}') as Record<string, unknown>;
    } catch (err) {
      // Malformed chip markup — ignore the click rather than break the page.
      console.warn('[help-chat] unreadable action params on chip', actionId, err);
      return;
    }
    const handler = this.actionRegistry.resolve(actionId);
    if (!handler) return;

    chip.disabled = true;
    let outcome: 'success' | 'failed' | 'cancelled';
    let detail: string | undefined;
    try {
      const r = await handler.run(params);
      outcome = r.outcome;
      detail = r.detail;
    } catch (err) {
      outcome = 'failed';
      detail = (err as Error)?.message ?? 'unknown error';
    }
    void this.chat.recordActionClick({ messageId, actionId, params, outcome });

    chip.disabled = false;
    chip.classList.remove('sc-action-chip--used');
    if (outcome === 'failed' && detail) console.warn('[help-chat] action failed:', detail);
  }
}

// Helpers — kept at module scope so they don't share `this` and stay
// straightforward to reason about. The chip HTML uses data attributes that
// the runtime click handler reads back; the canonicalizer matches the
// server-side `canon()` so `usedChipKeys` keys round-trip cleanly.

interface ChipInput extends ProposedAction {
  used: boolean;
}

export function renderHtmlWithChips(text: string, chips: ChipInput[]): string {
  const sorted = [...chips].sort((a, b) => a.offset - b.offset);
  let out = '';
  let cursor = 0;
  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i];
    const offset = Math.max(cursor, Math.min(a.offset, text.length));
    out += escapeHtml(text.slice(cursor, offset));
    out += chipHtml(a, i);
    cursor = offset;
  }
  out += escapeHtml(text.slice(cursor));
  return out;
}

function chipHtml(a: ChipInput, idx: number): string {
  const disabled = a.used ? 'disabled aria-disabled="true"' : '';
  const usedClass = a.used ? ' sc-action-chip--used' : '';
  const categoryClass = ` sc-action-chip--${a.category}`;
  const iconClass = `sc-action-chip__icon sc-action-chip__icon--${a.category}`;
  return (
    `<button class="sc-action-chip${usedClass}${categoryClass}" type="button" aria-label="${escapeHtml(a.label)}" ${disabled}` +
    ` data-message-id="${escapeHtml(a.messageId)}" data-action-idx="${idx}"` +
    ` data-action-id="${escapeHtml(a.id)}"` +
    ` data-action-params='${escapeHtml(JSON.stringify(a.params ?? {}))}'>` +
    `<span class="${iconClass}" aria-hidden="true">${iconFor(a.category)}</span>` +
    `<span class="sc-action-chip__label">${escapeHtml(a.label)}</span>` +
    `</button> `
  );
}

function iconFor(category: ProposedAction['category']): string {
  switch (category) {
    case 'support':
      return '✉'; // envelope
    case 'account':
      return '🔑'; // key
    case 'navigation':
      return '→'; // right arrow
  }
}

function escapeHtml(s: string): string {
  // Tolerate a missing field (a chip without a label/messageId) rather than
  // throwing out of the renderer and taking the whole answer with it.
  if (s === null || s === undefined) return '';
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      (
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }) as Record<string, string>
      )[c] ?? c
  );
}

function canonJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonJson).join(',')}]`;
  const obj = v as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => JSON.stringify(k) + ':' + canonJson(obj[k]))
    .join(',')}}`;
}

export function stripInlineActionTags(text: string): string {
  return text
    .replace(/<action\b[^>]*\/>/gi, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
